// WP-1.20 (S-39 Financial, ST-1): base currency (read-only — locked after first use), reporting
// year, number grouping, thresholds (in-transit alert days, minimum reserve, Mode-A cap), and
// an indicative-rate manual override (reusing the WP-1.6 FX set endpoint). All persisted via the
// settings store; changes take effect where each is consumed.
// WP-7.4 (RE-5) adds the Zakat section — off by default, the owner's own nisab/rate ("user-
// configured rules"), never a hardcoded methodology this app takes a position on.
import { CURRENCIES, parseAmountInput, type CurrencyCode } from '@pfos/engine';
import { useEffect, useState } from 'react';
import {
  checkboxClass,
  Field,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../../components/FormKit';
import { useSetFxRate } from '../../lib/fx';
import { useSettings, useSetSetting } from '../../lib/settings';

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

export function SettingsFinancial(): React.JSX.Element {
  const { data: settings } = useSettings();
  const setSetting = useSetSetting();
  const setFxRate = useSetFxRate();

  const baseCurrency = asString(settings?.baseCurrency, 'PKR') as CurrencyCode;
  const [fiscalYearStart, setFiscalYearStart] = useState('jan');
  const [numberGrouping, setNumberGrouping] = useState('lakhCrore');
  const [inTransitDays, setInTransitDays] = useState('5');
  const [minReserve, setMinReserve] = useState('');
  const [modeACap, setModeACap] = useState('');
  const [ambiguityGap, setAmbiguityGap] = useState('');
  const [zakatEnabled, setZakatEnabled] = useState(false);
  const [zakatNisab, setZakatNisab] = useState('');
  const [zakatRate, setZakatRate] = useState('2.5');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings === undefined) return;
    setFiscalYearStart(asString(settings.fiscalYearStart, 'jan'));
    setNumberGrouping(asString(settings.numberGrouping, 'lakhCrore'));
    if (typeof settings.inTransitAlertDays === 'number')
      setInTransitDays(String(settings.inTransitAlertDays));
    if (typeof settings.ambiguityGapThreshold === 'number')
      setAmbiguityGap(String(settings.ambiguityGapThreshold));
    if (typeof settings.zakatEnabled === 'boolean') setZakatEnabled(settings.zakatEnabled);
    if (typeof settings.zakatRatePercent === 'number')
      setZakatRate(String(settings.zakatRatePercent));
  }, [settings]);

  const save = (): void => {
    setSaved(false);
    setSetting.mutate({ key: 'fiscalYearStart', value: fiscalYearStart });
    setSetting.mutate({ key: 'numberGrouping', value: numberGrouping });
    const days = Number(inTransitDays);
    if (Number.isInteger(days) && days > 0)
      setSetting.mutate({ key: 'inTransitAlertDays', value: days });
    if (minReserve.trim() !== '') {
      const parsed = parseAmountInput(minReserve, { defaultCurrency: baseCurrency });
      if (parsed.ok)
        setSetting.mutate({ key: 'minimumReserveMinor', value: parsed.money.minorUnits });
    }
    if (modeACap.trim() !== '') {
      const parsed = parseAmountInput(modeACap, { defaultCurrency: baseCurrency });
      if (parsed.ok) setSetting.mutate({ key: 'modeACapMinor', value: parsed.money.minorUnits });
    }
    const gap = Number(ambiguityGap);
    if (Number.isFinite(gap) && gap >= 0 && gap <= 1)
      setSetting.mutate({ key: 'ambiguityGapThreshold', value: gap });
    setSetting.mutate({ key: 'zakatEnabled', value: zakatEnabled });
    if (zakatNisab.trim() !== '') {
      const parsed = parseAmountInput(zakatNisab, { defaultCurrency: baseCurrency });
      if (parsed.ok) setSetting.mutate({ key: 'zakatNisabMinor', value: parsed.money.minorUnits });
    }
    const rate = Number(zakatRate);
    if (Number.isFinite(rate) && rate > 0)
      setSetting.mutate({ key: 'zakatRatePercent', value: rate });
    setSaved(true);
  };

  // --- manual rate override (ST-1) ---
  const [rateFrom, setRateFrom] = useState<CurrencyCode>('USD');
  const [rateTo, setRateTo] = useState<CurrencyCode>('PKR');
  const [rate, setRate] = useState('');
  const [rateSaved, setRateSaved] = useState(false);

  return (
    <div className="space-y-6">
      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-4 text-sm font-medium text-ink">General</h2>

        <Field label="Base currency (locked after first use)" htmlFor="fin-base">
          <input id="fin-base" className={inputClass} value={baseCurrency} readOnly />
        </Field>

        <Field label="Reporting year" htmlFor="fin-year">
          <select
            id="fin-year"
            className={selectClass}
            value={fiscalYearStart}
            onChange={(e) => {
              setFiscalYearStart(e.target.value);
            }}
          >
            <option value="jan">Calendar (Jan–Dec)</option>
            <option value="jul">Fiscal (Jul–Jun)</option>
          </select>
        </Field>

        <Field label="Number grouping" htmlFor="fin-grouping">
          <select
            id="fin-grouping"
            className={selectClass}
            value={numberGrouping}
            onChange={(e) => {
              setNumberGrouping(e.target.value);
            }}
          >
            <option value="lakhCrore">Lakh / crore (1,25,000)</option>
            <option value="standard">Standard (125,000)</option>
          </select>
        </Field>

        <Field label="Stale in-transit alert (days)" htmlFor="fin-intransit">
          <input
            id="fin-intransit"
            type="number"
            min={1}
            className={inputClass}
            value={inTransitDays}
            onChange={(e) => {
              setInTransitDays(e.target.value);
            }}
          />
        </Field>

        <Field label="Minimum reserve (low-Spendable alert)" htmlFor="fin-reserve">
          <input
            id="fin-reserve"
            className={inputClass}
            placeholder={`e.g. ${baseCurrency} 50000`}
            value={minReserve}
            onChange={(e) => {
              setMinReserve(e.target.value);
            }}
          />
        </Field>

        <Field label="Mode-A auto-record cap" htmlFor="fin-modea">
          <input
            id="fin-modea"
            className={inputClass}
            placeholder={`e.g. ${baseCurrency} 5000`}
            value={modeACap}
            onChange={(e) => {
              setModeACap(e.target.value);
            }}
          />
        </Field>

        <Field label="Candidate-match ambiguity threshold (AU-8)" htmlFor="fin-ambiguity">
          <input
            id="fin-ambiguity"
            type="number"
            min={0}
            max={1}
            step={0.01}
            className={inputClass}
            placeholder="0.1"
            value={ambiguityGap}
            onChange={(e) => {
              setAmbiguityGap(e.target.value);
            }}
          />
        </Field>

        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={setSetting.isPending}
          onClick={save}
        >
          Save
        </button>
        {saved && <span className="ml-2 text-sm text-success">Saved</span>}
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Zakat (optional)</h2>
        <p className="mb-3 text-xs text-muted">
          Off by default and informational only (RE-5). This app does not apply a fixed methodology
          — set your own nisab threshold and rate below.
        </p>
        <label className="mb-3 flex items-center gap-1.5 text-sm text-ink">
          <input
            type="checkbox"
            className={checkboxClass}
            checked={zakatEnabled}
            onChange={(e) => {
              setZakatEnabled(e.target.checked);
            }}
          />
          Enable Zakat view
        </label>
        <Field label="Nisab threshold" htmlFor="fin-zakat-nisab">
          <input
            id="fin-zakat-nisab"
            className={inputClass}
            placeholder={`e.g. ${baseCurrency} 50000`}
            value={zakatNisab}
            onChange={(e) => {
              setZakatNisab(e.target.value);
            }}
          />
        </Field>
        <Field label="Zakat rate (%)" htmlFor="fin-zakat-rate">
          <input
            id="fin-zakat-rate"
            type="number"
            min={0}
            step={0.01}
            className={inputClass}
            value={zakatRate}
            onChange={(e) => {
              setZakatRate(e.target.value);
            }}
          />
        </Field>
        <p className="text-xs text-muted">Saved by the same Save button above.</p>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Indicative-rate override</h2>
        <p className="mb-3 text-xs text-muted">
          No live rate feed is configured; set a manual indicative rate here (FX-3). Used only for
          the estimate shown on cross-currency transfers, never to revalue recorded amounts.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <select
            aria-label="Rate from"
            className={`${selectClass} w-auto`}
            value={rateFrom}
            onChange={(e) => {
              setRateFrom(e.target.value as CurrencyCode);
            }}
          >
            {Object.keys(CURRENCIES).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="pb-2 text-sm text-muted">→</span>
          <select
            aria-label="Rate to"
            className={`${selectClass} w-auto`}
            value={rateTo}
            onChange={(e) => {
              setRateTo(e.target.value as CurrencyCode);
            }}
          >
            {Object.keys(CURRENCIES).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            aria-label="Rate"
            className={`${inputClass} w-32`}
            placeholder="Rate"
            value={rate}
            onChange={(e) => {
              setRate(e.target.value);
            }}
          />
          <button
            type="button"
            className={primaryButtonInlineClass}
            disabled={setFxRate.isPending}
            onClick={() => {
              const value = Number(rate);
              if (!(value > 0)) return;
              setFxRate.mutate(
                { from: rateFrom, to: rateTo, rate: value },
                {
                  onSuccess: () => {
                    setRateSaved(true);
                    setRate('');
                  },
                },
              );
            }}
          >
            Set rate
          </button>
          {rateSaved && <span className="text-sm text-success">Saved</span>}
        </div>
      </section>
    </div>
  );
}
